import {
    useWatch,
    type Control,
    type FieldValues,
    type Path
} from 'react-hook-form';
import type { UserProfileDocType } from '@ast-data/shared';
import { toDayString } from '../../utils/formatDate';

/**
 * Who was employed when — what narrows a person picker to the people who could
 * have done the thing a record describes.
 *
 * A profile carries its first and last day employed (`employmentStart`,
 * `employmentEnd`, inclusive, '' = open). A record asks for a WINDOW: one day
 * for a Fund or a Tagebuch (its Aufnahmedatum), a span for a project (Beginn to
 * Ende). An account is offered when its employment overlaps the window.
 *
 * Only ever a narrowing of the OFFER: what a record already says stays valid
 * and pickable (see personPicker), and the name -> account link on save never
 * looks at dates — an old record must keep the link to somebody who has left.
 */

/** Days, 'YYYY-MM-DD'; strings compare in calendar order. */
export interface EmploymentWindow {
    from: string;
    to: string;
}

/** Before and after any date this app stores: an open end of a window. */
const OPEN_FROM = '0000-01-01';
const OPEN_TO = '9999-12-31';

/**
 * A window from two days, either of which may be ''. Both '' is no window at
 * all — nothing to narrow by, so nobody is filtered out.
 */
export function employmentWindow(
    from: string,
    to: string = from
): EmploymentWindow | undefined {
    const first = from.trim();
    const last = to.trim();
    if (!first && !last) {
        return undefined;
    }
    return { from: first || OPEN_FROM, to: last || OPEN_TO };
}

/** Whether a profile's employment overlaps the window. No profile: yes. */
export function isEmployedDuring(
    profile:
        | Pick<UserProfileDocType, 'employmentStart' | 'employmentEnd'>
        | undefined,
    window: EmploymentWindow | undefined
): boolean {
    if (!window || !profile) {
        return true;
    }
    const start = profile.employmentStart?.trim() ?? '';
    const end = profile.employmentEnd?.trim() ?? '';
    if (start && window.to < start) {
        return false;
    }
    return !(end && window.from > end);
}

/**
 * The window of a form's date field — the local CALENDAR day of an
 * `recordedAt` datetime, or a day field as it is. Empty field: no window.
 */
export function useEmploymentDay<Values extends FieldValues>(
    control: Control<Values>,
    name: Path<Values>
): EmploymentWindow | undefined {
    const value: unknown = useWatch({ control, name });
    return employmentWindow(toDayString(value));
}
